import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { Box } from 'rebass';
import Button from '../../../../components/Button/Button';
import Modal, { ModalBody, ModalFooter } from '../../../../components/Modal';
import Form from '../../../../components/Form/Form';
import FormRow from '../../../../components/Form/FormRow';
import FormColumn from '../../../../components/Form/FormColumn';
import { Input, Label } from '../../../../components/Form';
import {
  PlatformPlan,
  PlatformProduct,
  PlatformProductInput,
  ProductType,
  ProductTypeAll,
  productTypeLabels,
} from './Plans.query';
import useNotify from '../../../../hooks/useNotify';
import {
  Table,
  TableHeaderCell,
  TableHeaderRow,
} from '../../../../components/Table/Table';
import { ProductRow } from './ProductRow';
import { GrowyTextarea } from '../../../../components/Form/GrowyTextarea';
import { gql } from '../../gql';

const CREATE_PLATFORM_PLAN = gql(`
  mutation CreatePlatformPlan($input: PlatformPlanInput!) {
    createPlatformPlan(input: $input) {
      id
      name
      description
      created
      updated
      products {
        id
        type
        price
        billingFrequency
        billingTiming
        billTo
      }
    }
  }
`);

const UPDATE_PLATFORM_PLAN = gql(`
  mutation UpdatePlatformPlan($id: ID!, $input: PlatformPlanUpdateInput!) {
    updatePlatformPlan(id: $id, input: $input) {
      id
      name
      description
      products {
        id
        type
        price
        billingFrequency
        billingTiming
        billTo
      }
    }
  }
`);

type PlanEditModalProps = {
  action: 'add' | 'edit';
  plan?: PlatformPlan | null;
  onClose: () => void;
  refetchQueries?: any[];
};

interface PlanFormData {
  name: string;
  description: string;
  products: (PlatformProduct | PlatformProductInput)[];
}

const initPlanForm = (plan?: PlatformPlan | null): PlanFormData => ({
  name: plan?.name || '',
  description: plan?.description || '',
  products: plan?.products?.length
    ? plan.products.map((p) => ({
        ...p,
        price: typeof p.price === 'number' ? p.price : 0,
      }))
    : [],
});

const PlanEditModal: React.FC<PlanEditModalProps> = ({
  action,
  plan,
  onClose,
  refetchQueries = [],
}) => {
  const { showError, showSuccess } = useNotify();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<PlanFormData>(initPlanForm(plan));

  const handleProductChange = useCallback(
    (index: number, updatedProduct: PlatformProduct | PlatformProductInput) => {
      setFormData((prev) => ({
        ...prev,
        products: prev.products.map((p, i) =>
          i === index ? updatedProduct : p
        ),
      }));
    },
    []
  );

  const handleAddProduct = useCallback(() => {
    const newProduct: PlatformProductInput = {
      type: 'BACKFLOW_TEST',
      price: 0,
      billingFrequency: 'MONTHLY',
      billingTiming: 'IN_ARREARS',
      billTo: 'WATER_SUPPLIER',
    };

    setFormData((prev) => ({
      ...prev,
      products: [...prev.products, newProduct as PlatformProduct],
    }));
  }, []);

  const handleRemoveProduct = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index),
    }));
  }, []);

  const [createPlan] = useMutation(CREATE_PLATFORM_PLAN, {
    refetchQueries,
    onCompleted: () => {
      showSuccess(
        `Plan ${action === 'add' ? 'created' : 'updated'} successfully`
      );
      onClose();
    },
    onError: (error) => {
      showError(`Failed to ${action} plan: ${error.message}`);
    },
  });

  const [updatePlan] = useMutation(UPDATE_PLATFORM_PLAN, {
    refetchQueries,
    onCompleted: () => {
      showSuccess('Plan updated successfully');
      onClose();
    },
    onError: (error) => {
      showError(`Failed to update plan: ${error.message}`);
    },
  });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const validateProducts = (
    products: (PlatformProduct | PlatformProductInput)[]
  ): { isValid: boolean; error?: string } => {
    const typeCounts = new Map<ProductTypeAll, number>();
    let testTypeCount = 0;

    for (const product of products) {
      // Count occurrences of each product type
      typeCounts.set(product.type, (typeCounts.get(product.type) || 0) + 1);

      // Count test types (BACKFLOW_TEST or BACKFLOW_TEST_PASSED)
      if (['BACKFLOW_TEST', 'BACKFLOW_TEST_PASSED'].includes(product.type)) {
        testTypeCount++;
      }
    }

    // Check for duplicate product types
    for (const [type, count] of typeCounts.entries()) {
      if (count > 1) {
        return {
          isValid: false,
          error: `Duplicate product type found: ${productTypeLabels[type]}. Each product type can only be added once.`,
        };
      }
    }

    // Check for multiple test types
    if (testTypeCount > 1) {
      return {
        isValid: false,
        error: `Only one of "${productTypeLabels.BACKFLOW_TEST}" or "${productTypeLabels.BACKFLOW_TEST_PASSED}" can be added per plan.`,
      };
    }

    return { isValid: true };
  };

  const validateForm = (
    data: PlanFormData
  ): { isValid: boolean; error?: string } => {
    if (!data.name || data.name.trim() === '') {
      return { isValid: false, error: 'Plan name is required' };
    }

    const productsValidation = validateProducts(data.products);
    if (!productsValidation.isValid) {
      return productsValidation;
    }

    return { isValid: true };
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);

      // Validate form
      const validation = validateForm(formData);
      if (!validation.isValid) {
        showError(validation.error || 'Please fix the form errors');
        setIsSubmitting(false);
        return;
      }

      try {
        // Create a clean copy of products without __typename
        const cleanProducts = (formData.products || []).map(
          ({ __typename, ...product }) => ({
            ...product,
            // Ensure price is properly formatted as a number
            price:
              typeof product.price === 'string'
                ? parseFloat(product.price)
                : product.price,
          })
        );

        const input = {
          name: formData.name,
          description: formData.description || null,
          products: cleanProducts,
        };

        if (action === 'add') {
          await createPlan({
            variables: { input },
          });
        } else if (plan?.id) {
          await updatePlan({
            variables: {
              id: plan.id,
              input,
            },
          });
        }
      } catch (error) {
        console.error('Error saving plan:', error);
        // Error is already handled by the mutation hooks
      } finally {
        setIsSubmitting(false);
      }
    },
    [action, createPlan, formData, plan, updatePlan]
  );

  return (
    <Modal
      sx={{ width: ['100%', 925] }}
      title={`${action === 'add' ? 'Add' : 'Edit'} Plan`}
      onClose={onClose}
    >
      <ModalBody>
        <Form>
          <FormRow>
            <FormColumn>
              <Label htmlFor="name">Name</Label>
              <Input
                autoFocus
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter plan name"
                width="420px"
              />
            </FormColumn>
          </FormRow>

          <FormRow>
            <FormColumn>
              <Label htmlFor="description">Description</Label>
              <GrowyTextarea
                id="description"
                name="description"
                placeholder="Enter description"
                height={34}
                focusedHeight={66}
                value={formData.description}
                onChange={handleChange}
                width="100%"
              />
            </FormColumn>
          </FormRow>

          <FormRow>
            <FormColumn>
              <Label>Products</Label>
              <Table style={{ overflowX: 'auto' }}>
                <Box style={{ width: '100%', minWidth: '100%' }}>
                  {formData.products.length > 0 ? (
                    <>
                      <TableHeaderRow
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            '220px 110px 180px 150px 150px 60px',
                          width: '100%',
                        }}
                      >
                        <TableHeaderCell>Type</TableHeaderCell>
                        <TableHeaderCell>Price ($)</TableHeaderCell>
                        <TableHeaderCell>Bill To</TableHeaderCell>
                        <TableHeaderCell>Billing Frequency</TableHeaderCell>
                        <TableHeaderCell>Timing</TableHeaderCell>
                        <TableHeaderCell></TableHeaderCell>
                      </TableHeaderRow>
                      {formData.products.map((product, index) => (
                        <ProductRow
                          key={product.id}
                          product={product}
                          onChange={(updatedProduct) =>
                            handleProductChange(index, updatedProduct)
                          }
                          onRemove={() => handleRemoveProduct(index)}
                        />
                      ))}
                    </>
                  ) : (
                    <Box
                      sx={{
                        gridColumn: '1 / -1',
                        padding: 4,
                        textAlign: 'center',
                        color: '#666',
                      }}
                    >
                      No products added yet. Click &#34;Add Product&#34; to get
                      started.
                    </Box>
                  )}
                </Box>
              </Table>
              <Button
                mt={2}
                icon="plus"
                alignSelf="flex-start"
                onClick={handleAddProduct}
                variant="primary"
                size="small"
              >
                Add Product
              </Button>
            </FormColumn>
          </FormRow>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          mr={2}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="primary"
          disabled={isSubmitting}
        >
          {action === 'add' ? 'Create Plan' : 'Save Changes'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

PlanEditModal.propTypes = {
  action: PropTypes.oneOf(['add', 'edit']).isRequired,
  plan: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  refetchQueries: PropTypes.array,
};

export default PlanEditModal;
